import { useState } from "react";
import { Send, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const SERVICE_OPTIONS = [
  "Budowa strony www",
  "Budowa sklepu",
  "Strona / Landing page w 24h",
  "Chatbot AI",
  "Pozycjonowanie",
  "Analityka",
  "Facebook Ads",
  "Google Ads",
  "Landing page",
  "Aplikacje na telefon",
  "Newsletter AI",
  "CMS",
  "Integracje (faktury, KSeF, marketplace, Google Merchant itp.)",
] as const;

interface ContactFormProps {
  compact?: boolean;
  onSuccess?: () => void;
}

const ContactForm = ({ compact = false, onSuccess }: ContactFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", website: "", message: "", consent: false });
  const [services, setServices] = useState<string[]>([]);

  const toggleService = (service: string) => {
    setServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data, error } = await supabase.from("contact_submissions").insert({
        name: form.name,
        company: form.company || null,
        email: form.email,
        phone: form.phone || null,
        website: form.website || null,
        message: form.message || null,
        services: services,
        consent: form.consent,
      }).select().single();
      if (error) throw error;
      // Trigger push notification to admins (fire and forget)
      supabase.functions.invoke("notify-new-submission", {
        body: { type: "contact", record: data },
      }).catch(() => {});
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        onSuccess?.();
      }, 3000);
      setForm({ name: "", company: "", email: "", phone: "", website: "", message: "", consent: false });
      setServices([]);
    } catch {
      toast.error("Wystąpił błąd przy wysyłaniu formularza. Spróbuj ponownie.");
    }
  };

  const inputClass = `w-full px-3 ${compact ? "py-2" : "py-2.5"} rounded-lg border border-input bg-background text-foreground text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring`;

  if (submitted) {
    return (
      <div className={`flex flex-col items-center justify-center ${compact ? "py-8" : "py-10"} gap-2`}>
        <CheckCircle className="text-secondary" size={compact ? 36 : 44} />
        <p className="text-base font-semibold text-foreground">Dziękujemy!</p>
        <p className="text-xs text-muted-foreground">Skontaktujemy się wkrótce.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-${compact ? "3" : "3"}`}>
      <input type="text" placeholder="Imię i nazwisko" required maxLength={100} value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
      <input type="text" placeholder="Nazwa firmy (opcjonalne)" maxLength={100} value={form.company}
        onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputClass} />
      <div className="grid grid-cols-2 gap-3">
        <input type="email" placeholder="E-mail" required maxLength={255} value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
        <input type="tel" placeholder="Telefon" maxLength={20} value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} />
      </div>
      <input type="url" placeholder="Adres obecnej strony www (opcjonalne)" maxLength={255} value={form.website}
        onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputClass} />
      <div>
        <p className="text-xs font-medium text-foreground mb-1.5">Czego potrzebujesz?</p>
        <div className="grid grid-cols-2 gap-2.5">
          {SERVICE_OPTIONS.slice(0, -1).map((service) => (
            <label key={service} className="flex items-center gap-2 cursor-pointer text-[11px] text-muted-foreground hover:text-foreground transition-colors">
              <input type="checkbox" checked={services.includes(service)} onChange={() => toggleService(service)}
                className="h-5 w-5 min-w-5 rounded border-input accent-primary" />
              <span>{service}</span>
            </label>
          ))}
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-[11px] text-muted-foreground hover:text-foreground transition-colors mt-1">
          <input type="checkbox" checked={services.includes(SERVICE_OPTIONS[SERVICE_OPTIONS.length - 1])} onChange={() => toggleService(SERVICE_OPTIONS[SERVICE_OPTIONS.length - 1])}
            className="h-5 w-5 min-w-5 rounded border-input accent-primary" />
          <span>{SERVICE_OPTIONS[SERVICE_OPTIONS.length - 1]}</span>
        </label>
      </div>
      <textarea placeholder="Dodatkowe informacje (opcjonalne)" rows={2} maxLength={1000} value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        className={`${inputClass} resize-none`} />
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" required checked={form.consent}
          onChange={(e) => setForm({ ...form, consent: e.target.checked })}
          className="mt-0.5 h-5 w-5 min-w-5 rounded border-input accent-primary" />
        <span className="text-[10px] text-muted-foreground leading-relaxed">
          Wyrażam zgodę na kontakt drogą mailową w celu przedstawienia wyceny i informacji o usługach BestSite. *
        </span>
      </label>
      <button type="submit"
        className={`w-full flex items-center justify-center gap-2 px-5 ${compact ? "py-2.5" : "py-3"} rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:opacity-90 transition-opacity`}>
        <Send size={14} />
        Wyślij zapytanie
      </button>
    </form>
  );
};

export default ContactForm;
